import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { add_workshop, list_workshops, update_workshop, delete_workshop } from '../../../utils/crm_services';
import Modal_proyectos from '../../menus/Modal';
import './WorkshopsEditor.css';

function WorkshopsEditor() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentWorkshopId, setCurrentWorkshopId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValues] = useState({
    name: '',
    objectives: '',
    datetime: '',
    templateFile: null,
    eventLink: ''
  });
  const [events, setEvents] = useState([]);

  const templateHint = useMemo(
    () => 'Sube un PDF base del certificado. Las coordenadas se configuran en la página de descarga.',
    []
  );

  const handleChange = (field) => (event) => {
    const value = field === 'templateFile' ? event.target.files?.[0] ?? null : event.target.value;
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const formatDateForDb = (datetimeValue) => {
    if (!datetimeValue) {
      return '';
    }
    if (datetimeValue.length === 16) {
      return `${datetimeValue.replace('T', ' ')}:00`;
    }
    return datetimeValue.replace('T', ' ');
  };

  const formatDateForInput = (dateValue) => {
    if (!dateValue) {
      return '';
    }
    return dateValue.replace(' ', 'T').slice(0, 16);
  };

  const formatDateForDisplay = (dateValue) => {
    if (!dateValue) {
      return '';
    }
    const parsed = new Date(dateValue.replace(' ', 'T'));
    if (Number.isNaN(parsed.getTime())) {
      return dateValue;
    }
    return parsed.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
  };

  const loadWorkshops = async () => {
    setIsLoading(true);
    const response = await list_workshops(token);
    if (response === 'INVALID') {
      navigate('/');
      return;
    }
    if (Array.isArray(response)) {
      setEvents(response);
    } else {
      setEvents([]);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (token) {
      loadWorkshops();
    }
  }, [token]);

  const resetForm = () => {
    setFormValues({
      name: '',
      objectives: '',
      datetime: '',
      templateFile: null,
      eventLink: ''
    });
    setCurrentWorkshopId(null);
    setIsEditing(false);
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (workshop) => {
    setFormValues({
      name: workshop.name || '',
      objectives: workshop.event_goals || '',
      datetime: formatDateForInput(workshop.event_date || ''),
      templateFile: null,
      eventLink: workshop.event_link || ''
    });
    setCurrentWorkshopId(workshop.id);
    setIsEditing(true);
    setModalOpen(true);
  };

  const handleCreateEventAsync = async (event) => {
    event.preventDefault();
    if (!formValues.name || !formValues.datetime || !formValues.objectives || !formValues.eventLink) {
      return;
    }
    if (!isEditing && !formValues.templateFile) {
      return;
    }

    const eventDate = formatDateForDb(formValues.datetime);
    const response = isEditing
      ? await update_workshop(
          token,
          currentWorkshopId,
          formValues.name,
          formValues.objectives,
          formValues.eventLink,
          eventDate,
          formValues.templateFile
        )
      : await add_workshop(
          token,
          formValues.name,
          formValues.objectives,
          formValues.eventLink,
          eventDate,
          formValues.templateFile
        );

    if (response === 'INVALID') {
      navigate('/');
      return;
    }

    await loadWorkshops();
    resetForm();
    setModalOpen(false);
  };

  const handleDeleteWorkshop = async (workshopId) => {
    const confirmed = window.confirm('¿Seguro que quieres eliminar este taller?');
    if (!confirmed) {
      return;
    }
    const response = await delete_workshop(token, workshopId);
    if (response === 'INVALID') {
      navigate('/');
      return;
    }
    await loadWorkshops();
  };

  const handleCopyShortUrl = async (shortUrl) => {
    if (!shortUrl) {
      return;
    }
    const url = `https://tomi.la/${shortUrl}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch (error) {
      console.error('No se pudo copiar el link.', error);
    }
  };

  return (
    <div className="container py-4 workshops-page">
      <Modal_proyectos isOpen={isModalOpen} onClose={() => setModalOpen(false)} title={isEditing ? 'Editar evento' : 'Nuevo evento'}>
        <form onSubmit={handleCreateEventAsync} className="d-flex flex-column h-100">
          <div className="modal-inner" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            <div className="mb-3">
              <label className="form-label">Nombre del evento</label>
              <input
                type="text"
                className="form-control"
                value={formValues.name}
                onChange={handleChange('name')}
                placeholder="Ej: TOMi y Canva"
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Descripción de objetivos</label>
              <textarea
                className="form-control"
                rows="3"
                value={formValues.objectives}
                onChange={handleChange('objectives')}
                placeholder="Describe los objetivos del evento"
              required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Fecha y hora</label>
              <input
                type="datetime-local"
                className="form-control"
                value={formValues.datetime}
                onChange={handleChange('datetime')}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Plantilla del certificado (PDF)</label>
              <input
                type="file"
                className="form-control"
                accept=".pdf"
                onChange={handleChange('templateFile')}
                required={!isEditing}
              />
              <div className="form-text">{templateHint}</div>
            </div>
            <div className="mb-3">
              <label className="form-label">Link del evento</label>
              <input
                type="url"
                className="form-control"
                value={formValues.eventLink}
                onChange={handleChange('eventLink')}
                placeholder="https://"
              required
              />
            </div>
          </div>
          <div className="modal-footer-fixed">
            <button type="button" className="btn btn-outline-secondary" onClick={() => setModalOpen(false)}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary">
              {isEditing ? 'Guardar cambios' : 'Crear evento'}
            </button>
          </div>
        </form>
      </Modal_proyectos>

      <div className="workshops-header d-flex align-items-center justify-content-between mb-3">
        <div>
          <h2 className="mb-1 text-white">Editor de Workshops</h2>
          <div className="text-white-50 small">Administra los talleres y accesos directos.</div>
        </div>
        <span className="badge bg-light text-dark border">Eventos creados</span>
      </div>
      <div className="card shadow-sm workshops-card">
        <div className="card-body p-0">
          <div className="table-responsive">
          <table className="table mb-0 workshops-table">
            <colgroup>
              <col style={{ width: '12%' }} />
              <col style={{ width: '22%' }} />
              <col style={{ width: '22%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '12%' }} />
            </colgroup>
            <thead className="table-light">
              <tr>
                <th>Fecha</th>
                <th>Evento</th>
                <th>Link</th>
                <th>Creador</th>
                <th>Short URL</th>
                <th className="text-end">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan="6" className="text-center text-muted py-4">
                    Cargando talleres...
                  </td>
                </tr>
              )}
              {!isLoading && events.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center text-muted py-4">
                    No hay talleres registrados.
                  </td>
                </tr>
              )}
              {!isLoading &&
                events.map((row) => (
                  <tr key={row.id}>
                    <td>{formatDateForDisplay(row.event_date)}</td>
                    <td className="fw-semibold">{row.name}</td>
                    <td>
                      <a href={row.event_link} target="_blank" rel="noreferrer" className="text-decoration-none">
                        {row.event_link}
                      </a>
                    </td>
                    <td>{row.created_by_name || '-'}</td>
                    <td>
                      {row.short_url ? (
                        <div className="d-flex align-items-center gap-2 short-url-cell">
                          <a href={`https://tomi.la/${row.short_url}`} target="_blank" rel="noreferrer">
                            {row.short_url}
                          </a>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary icon-btn"
                            onClick={() => handleCopyShortUrl(row.short_url)}
                            aria-label="Copiar short url"
                            title="Copiar"
                          >
                            📋
                          </button>
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="text-end">
                      <div className="d-flex justify-content-end gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary icon-btn"
                          onClick={() => openEditModal(row)}
                          aria-label="Editar"
                          title="Editar"
                        >
                          ✏️
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger icon-btn"
                          onClick={() => handleDeleteWorkshop(row.id)}
                          aria-label="Borrar"
                          title="Borrar"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          </div>
        </div>
      </div>
      <button
        type="button"
        className="btn btn-primary rounded-circle shadow floating-add-btn"
        onClick={openCreateModal}
        aria-label="Crear nuevo evento"
      >
        +
      </button>
    </div>
  );
}

export default WorkshopsEditor;
